import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { renderAsync } from 'docx-preview';
import * as mammoth from 'mammoth';
import {
  DocumentViewerConfig,
  SupportedFileType,
  DocumentStatus
} from '../types/document-viewer.types';

@Injectable({
  providedIn: 'root'
})
export class DocumentViewerService {
  private viewerStateSubject = new BehaviorSubject<{
    isOpen: boolean;
    config: DocumentViewerConfig | null;
    status: DocumentStatus;
  }>({
    isOpen: false,
    config: null,
    status: DocumentStatus.LOADING
  });

  public viewerState$ = this.viewerStateSubject.asObservable();

  constructor() {}

  /**
   * Determine file type from filename or MIME type
   */
  getFileType(fileName: string, mimeType?: string): SupportedFileType {
    const extension = fileName.toLowerCase().split('.').pop();

    const typeMap: { [key: string]: SupportedFileType } = {
      'pdf': 'pdf',
      'docx': 'docx',
      'doc': 'doc',
      'txt': 'txt',
      'text': 'txt',
      'xls': 'excel',
      'xlsx': 'excel',
      'png': 'image',
      'jpg': 'image',
      'jpeg': 'image',
      'gif': 'image',
      'webp': 'image',
      'bmp': 'image',
      'svg': 'image'
    };

    // Try MIME type first
    if (mimeType) {
      if (mimeType.includes('pdf')) return 'pdf';
      // .docx uses 'wordprocessingml', .doc uses 'msword'
      if (mimeType.includes('wordprocessingml')) return 'docx';
      if (mimeType.includes('msword')) return 'doc';
      // Excel files
      if (mimeType.includes('spreadsheetml')) return 'excel';
      if (mimeType.includes('ms-excel')) return 'excel';
      if (mimeType.includes('text')) return 'txt';
      if (mimeType.startsWith('image/')) return 'image';
    }

    // Fallback to extension-based detection
    return typeMap[extension || ''] || 'unknown';
  }

  /**
   * Open document in viewer
   */
  openDocument(config: DocumentViewerConfig): void {
    this.viewerStateSubject.next({
      isOpen: true,
      config,
      status: DocumentStatus.LOADING
    });
  }

  /**
   * Close document viewer
   */
  closeDocument(): void {
    this.viewerStateSubject.next({
      isOpen: false,
      config: null,
      status: DocumentStatus.LOADING
    });
  }

  /**
   * Update viewer status
   */
  updateStatus(status: DocumentStatus): void {
    const currentState = this.viewerStateSubject.value;
    this.viewerStateSubject.next({
      ...currentState,
      status
    });
  }

  /**
   * Render DOCX file using docx-preview
   */
  async renderDocx(
    blob: Blob,
    container: HTMLElement,
    useMammoth: boolean = false
  ): Promise<void> {
    try {
      if (useMammoth) {
        // Fallback to mammoth.js
        const arrayBuffer = await blob.arrayBuffer();
        const result = await mammoth.convertToHtml({ arrayBuffer });
        container.innerHTML = result.value;

        // Display conversion messages/warnings if any
        if (result.messages.length > 0) {
          console.warn('Mammoth conversion messages:', result.messages);
        }
      } else {
        // Primary: docx-preview
        await renderAsync(blob, container, null, {
          className: 'docx-wrapper',
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: false,
          renderHeaders: true,
          renderFooters: true,
          renderFootnotes: true,
          renderEndnotes: true,
          experimental: false
        });
      }
    } catch (error) {
      console.error('Error rendering DOCX:', error);
      throw error;
    }
  }

  /**
   * Render TXT file
   */
  async renderTxt(blob: Blob, container: HTMLElement): Promise<void> {
    try {
      const text = await blob.text();
      container.innerText = text;
    } catch (error) {
      console.error('Error rendering TXT:', error);
      throw error;
    }
  }

  /**
   * Fetch document from URL and return as Blob
   */
  async fetchDocument(url: string): Promise<Blob> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.blob();
    } catch (error) {
      console.error('Error fetching document:', error);
      throw error;
    }
  }

  /**
   * Download document to user's device
   */
  downloadDocument(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
}
